import { useHCaptchaStore } from "@faire/retailer-visitor-shared/stores/domain/HCaptchaStore";
import { useRefFrom } from "@faire/web/ui/hooks/useRefFrom";
import { ILoginRequest } from "@faire/web-api--source/indigofair/data/ILoginRequest";
import { useCallback, useEffect, useRef } from "react";

/**
 * Mimics the behavior of fetching the widgetID from hCaptcha asynchronously.
 *
 * Behind the scenes, it uses the useHCaptchaStore hook to get the widgetID/error
 * and wraps the response with a promise.
 */
export const useFetchWidgetID = () => {
  const resolveRef = useRef<((widgetID: string) => void) | null>(null);
  const rejectRef = useRef<
    ((error: ILoginRequest.IHCaptchaData.HCaptchaError) => void) | null
  >(null);
  const promiseRef = useRefFrom<Promise<string>>(
    () =>
      new Promise((resolve, reject) => {
        resolveRef.current = resolve;
        rejectRef.current = reject;
      })
  );

  const [{ widgetID, initializationError }, setHCaptchaStore] =
    useHCaptchaStore(["widgetID", "initializationError"]);

  const fetchWidgetID = useCallback(() => {
    return promiseRef.current;
  }, [promiseRef]);

  useEffect(() => {
    setHCaptchaStore({
      isHCaptchaEnabled: true,
    });
  }, [setHCaptchaStore]);

  useEffect(() => {
    if (widgetID) {
      resolveRef.current?.(widgetID);
    } else if (initializationError) {
      rejectRef.current?.(initializationError);
    }
  }, [initializationError, widgetID]);

  return { widgetID, widgetError: initializationError, fetchWidgetID };
};
